import { PaymentProvider } from "@brookbush/database";

import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { XIcon } from "lucide-react";
import { useRouter } from "next/compat/router";

import Button from "./Button";
import { useSubscriptionDB, useUserAction } from "~/hooks";
import useUserFlags from "~/hooks/useUserFlags";

const BackupPaymentReminderModal = () => {
	const { userFlags, updateFlag } = useUserFlags();

	const { userAction } = useUserAction();

	const { subscriptionDB } = useSubscriptionDB();
	const router = useRouter();

	const isOpen = Boolean(
		userFlags?.isBackupPaymentMethodModalRequired &&
			!userAction &&
			subscriptionDB?.paymentProvider === PaymentProvider.STRIPE,
	);

	const handleClose = async () => {
		await updateFlag({
			isBackupPaymentMethodModalRequired: false,
		});
	};

	return (
		<AnimatePresence>
			{isOpen && (
				<Dialog
					key="backup-payment-modal"
					as={motion.div}
					static
					open={isOpen}
					onClose={handleClose}
					className="relative z-50"
				>
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1, transition: { duration: 0.3 } }}
						exit={{ opacity: 0, transition: { duration: 0.2 } }}
						className="fixed inset-0 z-50 bg-black/40 dark:bg-black/50"
						aria-hidden="true"
						key="backup-payment-modal-backdrop"
					/>

					<div className="fixed inset-0 z-50 overflow-y-auto">
						<div className="flex min-h-full items-end justify-center sm:items-center sm:p-4 lg:p-10">
							<DialogPanel
								as={motion.div}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0, transition: { duration: 0.25 } }}
								exit={{ opacity: 0, y: 10, transition: { duration: 0.15 } }}
								className="relative mx-auto flex w-full max-w-md flex-col items-center gap-4 phablet:gap-6 rounded-t-xl border border-transparent bg-white px-4 pt-5 pb-10 shadow-2xl shadow-black/10 sm:rounded-2xl sm:px-8 sm:pt-6 sm:pb-8 dark:border-slate-600 dark:bg-slate-800 dark:shadow-slate-900"
							>
								<button
									className="absolute top-4 right-4 cursor-pointer rounded-full bg-slate-100 p-1 transition-all hover:brightness-95 dark:bg-slate-700"
									onClick={handleClose}
								>
									<XIcon size={20} />
								</button>

								<DialogTitle className="font-semibold phablet:text-2xl text-xl sm:-mb-2">
									Backup Payment Method
								</DialogTitle>

								<p className="text-center font-medium">
									Add a backup payment method to ensure uninterrupted access in
									case your primary payment method fails...
								</p>

								<div className="flex w-full gap-4">
									<Button
										onClick={handleClose}
										variant={"outlined"}
										className="flex-1"
									>
										Not Interested
									</Button>

									<Button
										className="flex-1"
										onClick={() => {
											handleClose();
											router?.push("/membership/add-payment-method");
										}}
									>
										Add Now
									</Button>
								</div>
							</DialogPanel>
						</div>
					</div>
				</Dialog>
			)}
		</AnimatePresence>
	);
};

export default BackupPaymentReminderModal;
